import React, { useState } from "react";
import { Phone } from "lucide-react";
import { Tables } from "@/types/database.types";
import { sanitizeBrandColor } from "@/lib/utils";

// Define custom organization type for UI-specific properties
type OrganizationWithUI = Tables<"organizations"> & {
  tagline?: string;
};

interface PublicAgentNavBarProps {
  organization?: OrganizationWithUI;
  agent?: Tables<"agents">;
  brandColor: string;
}

export const PublicAgentNavBar: React.FC<PublicAgentNavBarProps> = ({
  organization,
  agent,
  brandColor,
}) => {
  const [imageError, setImageError] = useState(false);

  // Sanitize brand color to prevent CSS injection
  const safeBrandColor = sanitizeBrandColor(brandColor);

  // Header text: always organization name
  const displayName = organization?.name || "Assistant";

  // Header image: organization logo only (no fallback to agent avatar)
  const avatarUrl = organization?.logo_url;

  // Safely access contact info from organization's JSON
  const contactInfo =
    (organization?.contact_info as Record<string, unknown>) || {};
  const phone =
    typeof contactInfo.phone === "string" ? contactInfo.phone : undefined;
  //const email =
  //  typeof contactInfo.email === "string" ? contactInfo.email : undefined;

  // Agent specific setting for phone number display
  // Show phone if requirePhoneNumber is true or undefined (default), hide if false.
  const agentContactInfo = agent?.contact_info as
    | { requirePhoneNumber?: boolean }
    | undefined;
  const shouldShowPhoneNumber = agentContactInfo?.requirePhoneNumber !== false;

  const handleImageError = () => {
    console.warn("Failed to load organization logo");
    setImageError(true);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backgroundColor: safeBrandColor,
        color: "#FFFFFF",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        width: "100vw",
        // Make sure header is always visible and extends properly
        height: "calc(env(safe-area-inset-top, 0px) + 64px)",
        zIndex: 9999,
        transform: "translate3d(0, 0, 0)",
        backfaceVisibility: "hidden",
      }}
    >
      {/* Safe area spacer - just background color, no content */}
      <div
        style={{
          height: "env(safe-area-inset-top, 0px)",
          backgroundColor: safeBrandColor,
        }}
      />

      {/* Actual header content */}
      <div
        className="flex items-center justify-between border-b border-white/10"
        style={{
          height: "64px",
          paddingLeft: "max(20px, env(safe-area-inset-left, 20px))",
          paddingRight: "max(20px, env(safe-area-inset-right, 20px))",
          backgroundColor: safeBrandColor,
        }}
      >
        {/* Organization Logo and Name */}
        <div className="flex items-center min-w-0 flex-1 mr-4">
          {avatarUrl && !imageError ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="h-10 w-10 mr-3 rounded-full bg-white/20 p-0.5 object-contain flex-shrink-0"
              onError={handleImageError}
            />
          ) : (
            <div className="h-10 w-10 mr-3 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">
                {displayName.substring(0, 2).toUpperCase()}
              </span>
            </div>
          )}
          <h1 className="text-white font-semibold text-sm truncate min-w-0">
            {displayName}
          </h1>
        </div>

        {/* Contact Icons */}
        <div className="flex gap-2 flex-shrink-0">
          {shouldShowPhoneNumber && phone && (
            <a
              href={`tel:${phone}`}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
              title={`Call ${displayName}`}
            >
              <Phone size={16} style={{ color: safeBrandColor }} />
            </a>
          )}
        </div>
      </div>
    </header>
  );
};
